const EXTENSION_ID = 'kgmofdjbolplmhoflgpnkeacjjffbohi'; // Example ID for local testing

/**
 * Checks if the extension is installed and has a PIN set.
 * @returns {Promise<{isInstalled: boolean, hasPin: boolean}>} A promise that resolves with the status.
 */
export const checkKeychainStatus = () => {
  return new Promise((resolve) => {
    // Check if the Chrome runtime is available
    if (!window.chrome || !window.chrome.runtime || !window.chrome.runtime.sendMessage) {
      return resolve({ isInstalled: false, hasPin: false });
    }

    try {
      window.chrome.runtime.sendMessage(
        EXTENSION_ID,
        { type: 'check_pin_status' },
        (response) => {
          if (window.chrome.runtime.lastError) {
            // This error occurs if the extension is not installed or disabled.
            console.log('Keychain extension not found or inactive.');
            resolve({ isInstalled: false, hasPin: false });
          } else {
            // The extension responded successfully.
            resolve(response || { isInstalled: false, hasPin: false });
          }
        }
      );
    } catch (e) {
      console.error("Error communicating with extension:", e);
      resolve({ isInstalled: false, hasPin: false });
    }
  });
};

/**
 * Sends a PIN to the extension to unlock credentials.
 * @param {string} pin - The 6-digit PIN entered by the user.
 * @returns {Promise<{username: string, password: string}>} A promise with credentials, or rejects on error.
 */
export const unlockKeychain = (pin) => {
    return new Promise((resolve, reject) => {
        if (!pin) {
          return reject(new Error("PIN cannot be empty."));
        }
        try {
          window.chrome.runtime.sendMessage(
            EXTENSION_ID,
            { type: 'request_unlock', pin: pin },
            (response) => {
              if (window.chrome.runtime.lastError) {
                return reject(new Error("Could not connect to the keychain. Is it installed and enabled?"));
              }
              if (response && response.error) {
                return reject(new Error(response.error));
              }
              if (response && response.username && response.password) {
                resolve(response);
              } else {
                reject(new Error('Unknown error during unlock.'));
              }
            }
          );
        } catch (e) {
          reject(e);
        }
    });
};

/**
 * Sends PIN and credentials to the extension for initial setup.
 * @param {string} pin - The 6-digit PIN to set.
 * @param {string} username - The username to store.
 * @param {string} password - The password to store.
 * @returns {Promise<{success: boolean, error?: string}>} A promise that resolves on success or rejects on failure.
 */
export const setupKeychain = (pin, username, password) => {
  return new Promise((resolve, reject) => {
    try {
      window.chrome.runtime.sendMessage(
        EXTENSION_ID,
        {
          type: 'setup_keychain',
          payload: { pin, username, password }
        },
        (response) => {
          if (window.chrome.runtime.lastError) {
            return reject(new Error("Could not connect to the keychain to complete setup."));
          }
          if (response && response.success) {
            resolve(response);
          } else {
            reject(new Error((response && response.error) || 'Failed to set up keychain.'));
          }
        }
      );
    } catch (e) {
      reject(e);
    }
  });
};